import { useState } from 'react';

import { MenuIcon } from '@/components/MenuIcon';
import { pendingSignings, useContractSignings } from '@/lib/contractSigning';
import { shortDateTime } from '@/lib/format';
import type { ContractSigning, Uuid } from '@/lib/types';
import { SignContractModal } from '@/pages/contract/SignContractModal';

/**
 * 홈 맨 위의 서명 요청 (V625). 본사가 계약서 서명을 요청하면 위젯보다 먼저 크게 선다 —
 * 「자료 › 계약」 까지 찾아 들어가야 아는 일이면 안 된다. [서명하기] 는 그 자리에서 서명 창을 연다.
 * 기다리는 것이 없으면 아무것도 세우지 않는다.
 */
export function SigningAlert({ siteId }: { siteId: Uuid }) {
  const { data = [] } = useContractSignings(siteId);
  const [signing, setSigning] = useState<ContractSigning | null>(null);
  const rows = pendingSignings(data);
  if (rows.length === 0) return null;

  return (
    <section
      className="rounded-xl p-4"
      style={{ background: 'var(--accent-soft, #eff6ff)', border: '1px solid var(--accent, #1e40af)' }}
    >
      <div className="mb-2 flex items-center gap-2 text-ui-md font-bold" style={{ color: 'var(--accent, #1e40af)' }}>
        <MenuIcon name="layout" size={16} />
        서명 요청 {rows.length}건
      </div>
      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li key={row.id} className="card flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="truncate text-ui-md font-medium">
                {row.title || '하도급계약서'} · {[row.trade, row.workName].filter(Boolean).join(' · ')}
              </div>
              <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                {row.originalName} · {row.sentByName ? `${row.sentByName} · ` : ''}
                {shortDateTime(row.sentAt)} 요청
              </div>
            </div>
            <button type="button" className="btn-primary shrink-0" onClick={() => setSigning(row)}>
              서명하기
            </button>
          </li>
        ))}
      </ul>
      {signing && (
        <SignContractModal
          siteId={siteId}
          signing={signing}
          onClose={() => setSigning(null)}
          onSigned={() => setSigning(null)}
        />
      )}
    </section>
  );
}
